'use client';

import { Button } from '@magicschool/spellbook/components/Button';
import { FilterChipDate } from '@magicschool/spellbook/components/FilterChipDate';
import { FilterChipSelect } from '@magicschool/spellbook/components/FilterChipSelect';
import { FilterChipToggle } from '@magicschool/spellbook/components/FilterChipToggle';
import { Label } from '@magicschool/spellbook/components/Label';
import { Text } from '@magicschool/spellbook/components/Text';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { Fragment, useCallback } from 'react';
import { useDataGridContext } from './DataGridContext';
import type { DataGridDateFilter, DataGridFilter, DataGridSelectFilter, DataGridToggleFilter } from './types';

interface DataGridFiltersProps {
  filters: DataGridFilter[];
  columnFilters: ColumnFiltersState;
  isLoading?: boolean;
  filtersLabel?: string;
  clearAllFilters?: string;
  onFilterChange: (filterId: string, value: unknown) => void;
  onClearAllFilters: () => void;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

// Helper to get filter value
const getFilterValue = (columnFilters: ColumnFiltersState, filterId: string): unknown => {
  const filter = columnFilters.find((f) => f.id === filterId);
  return filter?.value;
};

// Select Filter Component
const SelectFilter = ({
  filter,
  columnFilters,
  isLoading,
  onFilterChange,
}: {
  filter: DataGridSelectFilter;
  columnFilters: ColumnFiltersState;
  isLoading?: boolean;
  onFilterChange: (filterId: string, value: unknown) => void;
}) => {
  const currentValue = getFilterValue(columnFilters, filter.id);
  const selectValue =
    filter.multiple === false
      ? Array.isArray(currentValue)
        ? currentValue[0]
        : (currentValue as string)
      : Array.isArray(currentValue)
        ? currentValue
        : currentValue
          ? [currentValue as string]
          : [];

  const handleOnCheckedChange = useCallback(
    (selectedValues: string | string[] | undefined) => {
      onFilterChange(filter.id, selectedValues);

      if (!filter.onChange) return;

      if (filter.multiple === false) {
        if (selectedValues === undefined) {
          (filter.onChange as (value: string | undefined) => void)(undefined);
          return;
        }
        const singleValue: string = Array.isArray(selectedValues)
          ? selectedValues.length > 0
            ? String(selectedValues[0])
            : ''
          : typeof selectedValues === 'string'
            ? selectedValues
            : '';
        (filter.onChange as (value: string | undefined) => void)(singleValue);
      } else {
        if (selectedValues === undefined) return;
        const multiValue = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
        (filter.onChange as (values: string[]) => void)(multiValue);
      }
    },
    [filter, onFilterChange],
  );

  return (
    <Fragment>
      <Label className="sr-only" htmlFor={filter.id}>
        {filter.label}
      </Label>
      <FilterChipSelect
        id={filter.id}
        disabled={isLoading || filter.disabled}
        value={selectValue}
        onCheckedChange={handleOnCheckedChange}
        maxItemsToShow={filter.maxItemsToShow ?? 1}
        multiple={filter.multiple ?? true}
      >
        <FilterChipSelect.Trigger disabled={filter.disabled}>
          {filter.iconName && <FilterChipSelect.TriggerLeadingIcon name={filter.iconName} />}
          {filter.label}
        </FilterChipSelect.Trigger>
        <FilterChipSelect.Content>
          {filter.options.map((option) => (
            <FilterChipSelect.Item key={option.value} value={option.value}>
              <FilterChipSelect.ItemLabel>{option.label}</FilterChipSelect.ItemLabel>
            </FilterChipSelect.Item>
          ))}
        </FilterChipSelect.Content>
      </FilterChipSelect>
    </Fragment>
  );
};

// Toggle Filter Component
const ToggleFilter = ({
  filter,
  columnFilters,
  isLoading,
  onFilterChange,
}: {
  filter: DataGridToggleFilter;
  columnFilters: ColumnFiltersState;
  isLoading?: boolean;
  onFilterChange: (filterId: string, value: unknown) => void;
}) => {
  const currentValue = getFilterValue(columnFilters, filter.id);
  const toggleValue = Array.isArray(currentValue) && currentValue.length > 0 && Boolean(currentValue[0]);

  const handlePressedChange = useCallback(
    (pressed: boolean) => {
      onFilterChange(filter.id, pressed);
      filter.onChange?.(pressed);
    },
    [filter, onFilterChange],
  );

  return (
    <Fragment>
      <Label className="sr-only" htmlFor={filter.id}>
        {filter.label}
      </Label>
      <FilterChipToggle id={filter.id} disabled={isLoading || filter.disabled} pressed={toggleValue} onPressedChange={handlePressedChange}>
        {filter.iconName && <FilterChipToggle.LeadingIcon name={filter.iconName} />}
        {filter.label}
      </FilterChipToggle>
    </Fragment>
  );
};

// Date Filter Component
const DateFilter = ({
  filter,
  columnFilters,
  isLoading,
  onFilterChange,
}: {
  filter: DataGridDateFilter;
  columnFilters: ColumnFiltersState;
  isLoading?: boolean;
  onFilterChange: (filterId: string, value: unknown) => void;
}) => {
  const currentValue = getFilterValue(columnFilters, filter.id);
  let dateValue: Date | undefined;

  if (currentValue instanceof Date) {
    dateValue = currentValue;
  } else if (typeof currentValue === 'string') {
    const parsedDate = new Date(currentValue);
    dateValue = !isNaN(parsedDate.getTime()) ? parsedDate : undefined;
  } else if (Array.isArray(currentValue) && currentValue.length > 0) {
    const parsedDate = new Date(currentValue[0]);
    dateValue = !isNaN(parsedDate.getTime()) ? parsedDate : undefined;
  }

  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      onFilterChange(filter.id, date);
      filter.onChange?.(date ?? null);
    },
    [filter, onFilterChange],
  );

  return (
    <Fragment>
      <Label className="sr-only" htmlFor={filter.id}>
        {filter.label}
      </Label>
      <FilterChipDate value={dateValue} disabled={isLoading || filter.disabled} id={filter.id} onDateChange={handleDateChange}>
        <FilterChipDate.Trigger disabled={filter.disabled}>{filter.label}</FilterChipDate.Trigger>
        <FilterChipDate.Calendar />
      </FilterChipDate>
    </Fragment>
  );
};

// Main Filters Component
export function DataGridFilters<TData = unknown>({
  filters,
  columnFilters,
  isLoading,
  filtersLabel,
  clearAllFilters,
  onFilterChange,
  onClearAllFilters,
  searchInputRef,
}: DataGridFiltersProps) {
  const { returnFocusToTitleOrTable } = useDataGridContext<TData>();

  const handleClearClick = useCallback(() => {
    onClearAllFilters();

    // Return focus to search input if available, otherwise to table title or table
    requestAnimationFrame(() => {
      if (searchInputRef?.current) {
        searchInputRef.current.focus();
      } else {
        returnFocusToTitleOrTable();
      }
    });
  }, [onClearAllFilters, searchInputRef, returnFocusToTitleOrTable]);

  if (!filters.length) return null;

  return (
    <Fragment>
      {filtersLabel && <Text variant="label-2">{filtersLabel}</Text>}
      {filters.map((filter) => {
        if (filter.type === 'select') {
          return (
            <SelectFilter
              key={filter.id}
              filter={filter}
              columnFilters={columnFilters}
              isLoading={isLoading}
              onFilterChange={onFilterChange}
            />
          );
        }

        if (filter.type === 'toggle') {
          return (
            <ToggleFilter
              key={filter.id}
              filter={filter}
              columnFilters={columnFilters}
              isLoading={isLoading}
              onFilterChange={onFilterChange}
            />
          );
        }

        if (filter.type === 'date') {
          return (
            <DateFilter
              key={filter.id}
              filter={filter}
              columnFilters={columnFilters}
              isLoading={isLoading}
              onFilterChange={onFilterChange}
            />
          );
        }

        return null;
      })}
      {columnFilters.length > 0 && (
        <Button disabled={isLoading} color="neutral" variant="ghost" onClick={handleClearClick} className="self-end">
          <Button.LeadingIcon name="x" />
          {clearAllFilters}
        </Button>
      )}
    </Fragment>
  );
}
